import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Shield, FileText, Activity } from 'lucide-react'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { Header } from '@/shared/components/layout/Header'
import { StatusBar } from '@/shared/components/layout/StatusBar'
import { supabase } from '@/shared/lib/supabase'
import { NotFound } from './ElectronicSignatureLookup'
import { BiometricChart } from './BiometricChart'

export interface DigitalSignatureRecord {
  signature_id: string
  short_id?: string | null
  signature_timestamp: string
  original_filename: string | null
  signed_filename: string | null
  file_hash: string
  hash_algorithm: string
  signer_name: string | null
  signer_role: string | null
  certificate_subject: string | null
  certificate_issuer: string | null
  certificate_valid_from: string | null
  certificate_valid_to: string | null
  certificate_serial_number: string | null
  reason: string | null
  location: string | null
  biometric_data?: Array<{ x: number; y: number; t: number; pressure: number; pointerType: string }> | null
  signature_image_data?: string | null
}

/**
 * Vista pura — recibe el record por prop. La usa este componente (lookup
 * por /s/:id) y UnifiedSignatureLookup (lookup por /v/:shortId).
 */
export function DigitalSignatureView({ record }: { record: DigitalSignatureRecord }) {
  return (
    <div className="min-h-screen flex flex-col pb-7 bg-gradient-to-br from-legal-50 via-white to-blue-50 dark:from-legal-900 dark:via-legal-800 dark:to-legal-900">
      <Header />
      <main className="flex-1 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-legal-800 rounded-xl shadow-lg border border-green-200 dark:border-green-800 overflow-hidden">
          <div className="bg-green-50 dark:bg-green-900/30 border-b border-green-200 dark:border-green-800 p-6">
            <div className="flex items-center gap-3">
              <Shield className="text-green-600 dark:text-green-400" size={32} />
              <div>
                <h1 className="text-xl font-bold text-legal-900 dark:text-white">Firma Digital</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  art. 9 Ley 25.506 Argentina &mdash; con certificado PKI
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <Field label="Firmante" value={record.signer_name || record.certificate_subject || 'Sin nombre'} subtitle={record.signer_role || undefined} />
            <Field label="Fecha de firma" value={new Date(record.signature_timestamp).toLocaleString('es-AR')} />
            {record.original_filename && (
              <Field label="Documento original" value={record.original_filename} icon={<FileText size={14} />} />
            )}
            {record.certificate_subject && (
              <Field label="Sujeto del certificado" value={record.certificate_subject} mono />
            )}
            {record.certificate_issuer && (
              <Field label="Emisor del certificado" value={record.certificate_issuer} mono />
            )}
            {record.certificate_valid_from && record.certificate_valid_to && (
              <Field
                label="Validez del certificado"
                value={`${new Date(record.certificate_valid_from).toLocaleDateString('es-AR')} - ${new Date(record.certificate_valid_to).toLocaleDateString('es-AR')}`}
              />
            )}
            {record.certificate_serial_number && (
              <Field label="Número de serie" value={record.certificate_serial_number} mono />
            )}
            <Field label="Hash SHA-256 del PDF firmado" value={record.file_hash} mono />
            {record.reason && <Field label="Motivo" value={record.reason} />}
            {record.location && <Field label="Ubicación" value={record.location} />}
          </div>

          {(record.signature_image_data || (record.biometric_data && record.biometric_data.length > 0)) && (
            <div className="border-t border-legal-100 dark:border-legal-700 p-6 space-y-3">
              <h3 className="text-sm font-bold text-legal-900 dark:text-white flex items-center gap-2">
                <Activity size={14} />
                Firma dibujada con captura biométrica
              </h3>
              {record.signature_image_data && (
                <div className="bg-white border border-legal-200 dark:border-legal-700 rounded p-2 inline-block">
                  <img src={record.signature_image_data} alt="Firma dibujada" className="max-h-24" />
                </div>
              )}
              {record.biometric_data && record.biometric_data.length > 0 && (
                <BiometricChart samples={record.biometric_data} />
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {record.biometric_data?.length || 0} puntos capturados (posición, presión, tiempo).
                Datos disponibles para pericia caligráfica forense.
              </p>
            </div>
          )}

          <div className="bg-green-50 dark:bg-green-900/20 border-t border-green-200 dark:border-green-800 p-5 text-sm text-green-900 dark:text-green-200">
            <p className="font-medium mb-1">Implicancia legal</p>
            <p className="text-xs">
              Esta firma digital con certificado PKI tiene <strong>plena validez jurídica</strong> según el
              art. 9 de la Ley 25.506. Goza de presunción de autoría y de integridad del documento. El hash
              SHA-256 mostrado permite verificar que el PDF no fue modificado tras la firma.
            </p>
          </div>
        </div>

        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          Este registro es público y consultable en SignWiz. Para verificar integridad,
          calculá el SHA-256 del PDF y comparalo con el hash mostrado arriba.
        </p>
      </div>
      </main>
      <StatusBar />
    </div>
  )
}

export default function DigitalSignatureLookup() {
  const { id } = useParams<{ id: string }>()
  const [record, setRecord] = useState<DigitalSignatureRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id || !supabase) return
    supabase
      .from('sgw_signatures')
      .select('*')
      .eq('signature_id', id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else if (!data) setError('Firma no encontrada')
        else setRecord(data as DigitalSignatureRecord)
      })
      .then(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pb-7 bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <StatusBar />
      </div>
    )
  }
  if (error || !record) return <NotFound message={error || 'No existe una firma con ese ID.'} />
  return <DigitalSignatureView record={record} />
}

function Field({
  label, value, subtitle, mono, icon,
}: { label: string; value: string; subtitle?: string; mono?: boolean; icon?: React.ReactNode }) {
  return (
    <div className="bg-legal-50 dark:bg-legal-900/40 p-3 rounded-lg">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`text-legal-900 dark:text-white ${mono ? 'font-mono text-xs break-all' : 'text-sm font-medium'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  )
}
